import { brand } from "@brand";
import type { RequiredIntake, RenewalCheckinTask } from "@/lib/api/hooks";
import type { HomeTask } from "@/types/bootstrap";

const INTAKE_TASK_KINDS = new Set([
  "finish_intake",
  "complete_intake",
  "treatment_intake",
  "renewal_checkin",
]);

/**
 * Fold pending required intakes into the Home task list as first-class task
 * cards, dropping any generic backend intake task so the same work never
 * shows twice. Shared by the Home Tasks section and the landing
 * IntakeAttentionDrawer so the two always render the identical list.
 */
export function buildHomeTasks(
  homeTasks: HomeTask[],
  pendingIntakes: RequiredIntake[],
  renewalCheckins: RenewalCheckinTask[] = [],
): HomeTask[] {
  const intakeTasks: HomeTask[] = pendingIntakes.map((intake) => {
    const isCheckin = intake.intake_kind === "renewal_checkin";
    const isCorrection = intake.intake_kind === "intake_correction";
    return {
      id: `intake-${intake.awaiting_order_id}`,
      kind: intake.intake_kind,
      title: isCorrection
        ? `Update your ${intake.product_name} intake`
        : isCheckin
          ? "Complete your monthly check-in"
          : `Finish your ${intake.product_name} intake`,
      // Storefront: a licensed provider (never "your clinician"), and no
      // "script" slang.
      body: brand.storefrontOnboarding
        ? isCorrection
          ? "Your provider noticed a few answers that need a second look. Complete a new intake so they can finish your review."
          : isCheckin
            ? "Your provider needs a quick check-in on how treatment is going before your next prescription."
            : "A licensed provider needs your answers to these questions before they can prescribe."
        : isCorrection
          ? "Your clinician noticed a few answers that need a second look. Complete a new intake so they can finish your review."
          : isCheckin
            ? "Your clinician needs a quick symptom check-in before writing this month's prescription."
            : "Your clinician needs your medical questionnaire before they can write your script.",
      cta_label: isCorrection
        ? "Complete new intake"
        : isCheckin
          ? "Start check-in"
          : "Finish intake",
      cta_href: `/app/intake?start=${encodeURIComponent(intake.awaiting_order_id)}`,
      tone: null,
    };
  });
  const paidForms = new Set(pendingIntakes.map((intake) => intake.healthie_form_id));
  const renewalTasks: HomeTask[] = renewalCheckins
    .filter((checkin) => !paidForms.has(checkin.healthie_form_id))
    .map((checkin) => ({
      id: checkin.id,
      kind: "renewal_checkin",
      title: `Complete your ${checkin.product_name} check-in`,
      body: brand.storefrontOnboarding
        ? "Your plan renews soon. Share how treatment is going and any requested changes before your next refill. Your provider reviews dose changes after renewal payment."
        : "Your plan renews soon. Share how treatment is going and any requested changes before your next refill. Your clinician reviews dose changes after renewal payment.",
      cta_label: "Start check-in",
      cta_href: `/app/intake?product=${encodeURIComponent(checkin.product_id)}`,
      tone: null,
    }));
  // Concrete per-product intake cards supersede any generic backend intake
  // task, and lead the list (the clinician is waiting on them).
  return [
    ...intakeTasks,
    ...renewalTasks,
    ...homeTasks.filter((t) => !INTAKE_TASK_KINDS.has(t.kind)),
  ];
}
